import React, { useEffect, useState } from 'react';
import TimerSurfaceService from '../../services/TimerSurfaceService';
import { extractTimedWaitBounds, recipeTimerId, recipeTimerRegistry, recipeTimerPersistenceAvailable } from './recipeStepTimers';
import { recipeTimerSession } from './recipeTimerIdentity';
import './RecipeStepTimer.css';

const format = ms => {
  const seconds = Math.ceil(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};
const durationLabel = ms => ms % 3600000 === 0 ? `${ms / 3600000} hr` : ms % 60000 === 0 ? `${ms / 60000} min` : `${ms / 1000} sec`;
const rangeLabel = (firstMs, finalMs) => firstMs % 60000 === 0 && finalMs % 60000 === 0
  ? `${firstMs / 60000}–${finalMs / 60000} min` : `${durationLabel(firstMs)}–${durationLabel(finalMs)}`;

export default function RecipeStepTimer({ userId, mealId, instructions, index }) {
  const bounds = extractTimedWaitBounds(instructions?.[index]);
  const durationMs = bounds?.finalMs;
  const isRange = bounds && bounds.firstMs < bounds.finalMs;
  const id = recipeTimerId(userId, mealId, instructions, index);
  const [, setTime] = useState(Date.now());
  const [authorized, setAuthorized] = useState(() => recipeTimerSession.canPublish(userId));
  const [alarmStatus, setAlarmStatus] = useState(() => recipeTimerSession.getAlarmStatus(userId));
  useEffect(() => {
    const refreshAuthorization = () => {
      setAuthorized(recipeTimerSession.canPublish(userId));
      setAlarmStatus(recipeTimerSession.getAlarmStatus(userId));
    };
    window.addEventListener('recipe-timer-authorization-changed', refreshAuthorization);
    refreshAuthorization();
    return () => window.removeEventListener('recipe-timer-authorization-changed', refreshAuthorization);
  }, [userId]);
  useEffect(() => {
    if (!id || !durationMs) return undefined;
    const refresh = () => setTime(Date.now());
    const onChange = event => { if (event?.detail?.scopeId) refresh(); };
    window.addEventListener('recipe-timers-changed', onChange);
    const interval = setInterval(refresh, 500);
    return () => {
      clearInterval(interval);
      window.removeEventListener('recipe-timers-changed', onChange);
    };
  }, [id, durationMs]);
  const entry = id ? recipeTimerRegistry.get(userId, id) : null;
  if (!durationMs || !id) return null;
  const remainingMs = entry?.remainingMs ?? durationMs;
  const firstReached = Boolean(isRange && entry?.firstReached);
  const shownRemainingMs = isRange && !firstReached ? Math.max(0, remainingMs - (durationMs - bounds.firstMs)) : remainingMs;
  const state = entry?.completed ? 'Done' : entry?.running ? 'Pause' : remainingMs < durationMs ? 'Resume' : 'Start';
  const change = () => {
    if (!authorized) return;
    if (state === 'Start') recipeTimerRegistry.start(userId, id, `Step ${index + 1}`, durationMs, bounds.firstMs);
    if (state === 'Pause') recipeTimerRegistry.pause(userId, id);
    if (state === 'Resume') recipeTimerRegistry.resume(userId, id);
  };
  return (
    <div className={`recipe-step-timer${entry?.completed ? ' complete' : firstReached ? ' warning' : ''}${isRange ? ' ranged' : ''}`} data-step={index + 1}>
      <div className="recipe-step-timer-row">
        <span className="recipe-step-timer-icon" aria-hidden="true">◷</span>
        <div className="recipe-step-timer-readout"><small>Step {index + 1} · {isRange ? rangeLabel(bounds.firstMs, durationMs) : durationLabel(durationMs)} timer</small><strong role="timer" aria-label={`Step ${index + 1} remaining to ${firstReached ? 'latest' : 'first'} alert`}>{format(shownRemainingMs)}</strong></div>
        <button type="button" className="recipe-step-timer-primary" disabled={!authorized || state === 'Done'} onClick={change}>{state}</button>
        <button type="button" className="recipe-step-timer-reset" disabled={!authorized} aria-label={`${isRange && entry?.running ? 'Stop' : 'Reset'} step ${index + 1} timer`} onClick={() => recipeTimerRegistry.reset(userId, id)}>{isRange && entry?.running ? '■' : '↺'}</button>
      </div>
      {firstReached && !entry?.completed && <span className="recipe-step-timer-warning" role="status">Minimum reached · check food; timer continues to {durationLabel(durationMs)}</span>}
      {entry?.completed && <span className="recipe-step-timer-done" role="status">Time’s up</span>}
      {!authorized && <span className="recipe-step-timer-caveat" role="status">Setting up timers for this account…</span>}
      {TimerSurfaceService.isNative() && alarmStatus?.notificationsAllowed === false && <span className="recipe-step-timer-caveat" role="status">Device notifications off; enable notifications in Android settings for background alerts.</span>}
      {TimerSurfaceService.isNative() && alarmStatus?.exactAlarmAllowed === false && <span className="recipe-step-timer-caveat" role="status">Exact alarms off; allow exact alarms in Android settings to avoid delayed alerts.</span>}
      {TimerSurfaceService.isNative() && alarmStatus?.ok === false && <span className="recipe-step-timer-caveat" role="status">Device alerts unavailable; keep the app open.</span>}
      {!recipeTimerPersistenceAvailable && <span className="recipe-step-timer-caveat" role="status">Timer storage unavailable; timers may not survive refresh.</span>}
      {!TimerSurfaceService.isNative() && <span className="recipe-step-timer-caveat">Sound alerts while app is open only</span>}
    </div>
  );
}
